import { Link } from "react-router-dom";
import { ArrowLeft, Star, Clock, Search, ChevronUp, ChevronDown, BookOpen, X, MapPin } from "lucide-react";
import { getCatalogLabel } from "../../lib/catalogLabel";
import { computeEtaMinutes, formatDeliveryEta } from "../../lib/deliveryEta";
import ProductCard from "../../components/ProductCard";
import BannerCarouselDynamic from "../../components/BannerCarouselDynamic";
import { FullPageLoader } from "../../components/Loaders";
import { useIsBlinkit } from "../../lib/storefrontTheme";
import { useShopController } from "./controllers/useShopController";
import BkShopView from "./blinkit/BkShopView";

export default function RestaurantDetailPage() {
    return useIsBlinkit() ? <BkShopView /> : <ClassicRestaurantDetailPage />;
}

function ClassicRestaurantDetailPage() {
    const {
        id, tenant, rd, verticals, q, setQ, collapsed, setCollapsed, foodType, setFoodType,
        menuOpen, setMenuOpen, sectionRefs, addToCart, showVegFilter, groups, totalItems, scrollTo,
    } = useShopController();

    if (!rd) return <FullPageLoader />;
    const r = rd.restaurant;
    const catalogLabel = getCatalogLabel(r, verticals);
    const catalogLabelUpper = catalogLabel.toUpperCase();

    return (
        <div className="pb-32 relative">
            {r.cover_image_url ? (
                <div className="relative">
                    <img src={r.cover_image_url} alt={r.name} className="w-full h-56 md:h-72 lg:h-80 object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-transparent to-transparent" />
                    <Link to="/" data-testid="rest-back" className="absolute top-4 left-4 bg-card/90 backdrop-blur-md rounded-full p-2"><ArrowLeft size={20} /></Link>
                </div>
            ) : (
                <div className="px-4 pt-4 flex items-center gap-3">
                    <Link to="/" data-testid="rest-back" className="bg-card rounded-full p-2 border border-border"><ArrowLeft size={20} /></Link>
                </div>
            )}

            <div className={`bg-card ${r.cover_image_url ? "-mt-8" : "mt-3"} mx-4 rounded-3xl p-5 border border-border relative`}>
                <h1 className="text-2xl font-display font-bold" data-testid="rest-name">{r.name}</h1>
                <p className="text-sm text-muted-foreground mt-0.5 line-clamp-2">{r.description || (r.cuisines || []).join(" • ")}</p>
                <div className="flex items-center gap-4 mt-3 text-sm flex-wrap">
                    {/* iter128 — Show rating chip only when at least one customer
                        has rated. No fake "4.0" baseline. */}
                    {(r.rating_count > 0 && typeof r.rating === "number") ? (
                        <>
                            <span className="inline-flex items-center gap-1 bg-emerald-700 text-white px-2 py-0.5 rounded-md font-semibold">{r.rating.toFixed(1)} <Star size={11} className="fill-white" /></span>
                            <span className="text-muted-foreground">{r.rating_count}+ ratings</span>
                        </>
                    ) : null}
                    {(() => {
                        // BUG-FIX: factor in r.distance_km (shown right below as
                        // "X km away") so this doesn't show the same ETA as a
                        // shop several km further away — see computeEtaMinutes.
                        const etaText = formatDeliveryEta(tenant, computeEtaMinutes(r.avg_prep_minutes, r.distance_km));
                        return etaText ? (
                            <span
                                data-testid="rest-eta"
                                className="inline-flex items-center gap-1 text-muted-foreground"
                            ><Clock size={14} /> {etaText}</span>
                        ) : null;
                    })()}
                    {typeof r.distance_km === "number" && (
                        <span
                            data-testid="rest-distance"
                            className="inline-flex items-center gap-1 text-muted-foreground"
                        >
                            <MapPin size={14} /> {r.distance_km.toFixed(1)} km away
                        </span>
                    )}
                </div>
            </div>

            {/* Search + Veg/Non-Veg quick filter (food / meat / grocery only) */}
            <div className="px-4 mt-5 space-y-3">
                <div className="flex items-center bg-muted rounded-xl px-4 py-2.5">
                    <Search size={16} className="text-muted-foreground" />
                    <input data-testid="rest-search" placeholder={`Search this ${catalogLabel.toLowerCase()}`} value={q} onChange={(e) => setQ(e.target.value)} className="flex-1 bg-transparent outline-none ml-2 text-sm" />
                </div>
                {showVegFilter && (
                    <div className="flex items-center gap-2 flex-wrap">
                        <Chip on={!foodType} onClick={() => setFoodType(null)} data-testid="ft-all">All</Chip>
                        <Chip on={foodType === "VEG"} onClick={() => setFoodType("VEG")} data-testid="ft-veg">
                            <span className="food-mark veg mr-1.5 align-middle" /> Veg
                        </Chip>
                        <Chip on={foodType === "NON_VEG"} onClick={() => setFoodType("NON_VEG")} data-testid="ft-nonveg">
                            <span className="food-mark non-veg mr-1.5 align-middle" /> Non-veg
                        </Chip>
                    </div>
                )}
            </div>

            {/* iter101 — placement="SHOP" banners scoped to this shop. */}
            <BannerCarouselDynamic
                placement="SHOP"
                restaurantId={id}
                className="px-4 mt-3"
            />

            {/* Grouped category sections */}
            <div className="px-4 mt-3" data-testid="cat-sections">
                {groups.length === 0 ? (
                    <p className="text-center text-muted-foreground py-12 text-sm">{q || foodType ? "No items match your filters" : `${catalogLabel} coming soon`}</p>
                ) : groups.map((g) => {
                    const open = !collapsed[g.id];
                    return (
                        <section key={g.id} ref={(el) => { sectionRefs.current[g.id] = el; }} data-testid={`cat-section-${g.id}`} className="scroll-mt-20 border-b border-border last:border-0">
                            <button
                                data-testid={`cat-toggle-${g.id}`}
                                onClick={() => setCollapsed((c) => ({ ...c, [g.id]: open }))}
                                className="w-full flex items-center justify-between py-4 text-left"
                            >
                                <h2 className="text-lg font-display font-bold">{g.name} <span className="text-muted-foreground font-normal">({g.items.length})</span></h2>
                                {open ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                            </button>
                            {/* md+: two columns with a 96px centre gap — the floating catalogue pill below is fixed at
                                left-1/2 (64px wide), and each card's ADD / BOOK NOW sits under its image at the column's
                                right edge, i.e. the page centre for column 1. The gap keeps that lane free (≥14px clearance). */}
                            {open && (
                                <div className="md:grid md:grid-cols-2 md:gap-x-24 pb-2">
                                    {g.items.map((p) => <ProductCard key={p.id} product={p} restaurantId={id} onAdd={addToCart} isFood={showVegFilter} />)}
                                </div>
                            )}
                        </section>
                    );
                })}
            </div>

            {/* Floating MENU button */}
            {groups.length > 0 && (
                <button data-testid="menu-fab" onClick={() => setMenuOpen(true)}
                    className="fixed bottom-24 left-1/2 -translate-x-1/2 z-30 bg-foreground text-background rounded-full w-16 h-16 shadow-2xl flex flex-col items-center justify-center active:scale-95 transition">
                    <BookOpen size={18} />
                    <span className="text-[10px] font-bold tracking-wider mt-0.5" data-testid="menu-fab-label">{catalogLabelUpper}</span>
                </button>
            )}

            {/* Menu sheet (dark) */}
            {menuOpen && (
                <div className="fixed inset-0 z-50 bg-black/40 flex items-end justify-center" onClick={() => setMenuOpen(false)}>
                    <div className="bg-neutral-900 text-white w-full max-w-md md:max-w-lg rounded-t-3xl p-1 max-h-[70vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                        <div className="px-5 pt-4 pb-2 flex items-center justify-between">
                            <p className="text-xs uppercase tracking-[0.18em] text-white/60 font-bold" data-testid="menu-sheet-label">{catalogLabel} • {totalItems} items</p>
                            <button onClick={() => setMenuOpen(false)} className="p-1 rounded-full hover:bg-white/10"><X size={18} /></button>
                        </div>
                        <div className="px-2 pb-4 divide-y divide-white/10">
                            {groups.map((g) => (
                                <button key={g.id} data-testid={`menu-jump-${g.id}`} onClick={() => scrollTo(g.id)}
                                    className="w-full flex items-center justify-between px-3 py-3.5 hover:bg-white/5 rounded-xl">
                                    <span className="text-base font-semibold">{g.name}</span>
                                    <span className="text-base font-bold tabular-nums text-white/70">{g.items.length}</span>
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function Chip({ on, children, ...rest }) {
    return (
        <button {...rest}
            className={`text-xs font-semibold whitespace-nowrap px-3 py-1.5 rounded-full border ${on ? "bg-foreground text-background border-foreground" : "border-border text-muted-foreground"}`}>
            {children}
        </button>
    );
}
